import React from 'react';
import { format, isSameDay, subDays } from 'date-fns';
import { fromLocalISOString, toLocalISOString } from '@/utils/timezoneUtils';
import { CalendarEvent, DaySchedule } from '../../types';
import { useLanguage } from '@/contexts/LanguageContext';
import CalendarEventItem from './CalendarEventItem';
import { generateEventColor } from '../../utils/colorUtils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@/contexts/ThemeContext';
import { useColorIntensity } from '../../contexts/ColorIntensityContext';
import { fetchAllGuardSchedules } from '../../services/totalWorkService';
import { useShiftContextMenu } from '../../hooks/useShiftContextMenu';
import { useShiftApi } from '../../hooks/useShiftApi';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';

interface PerJobRowViewProps {
  daySchedules: DaySchedule[];
  guards: any[];
  handleDayClick: (date: Date) => void;
  handleEventClick: (event: CalendarEvent, e: React.MouseEvent, isMultiSelectMode?: boolean) => void;
  onDuplicateEvent: (event: CalendarEvent) => void;
  onDeleteEvent: (event: CalendarEvent) => void;
  onCopyShift: (event: CalendarEvent) => void;
  onCopyShiftWithoutGuards: (event: CalendarEvent) => void;
  onPasteShift: (targetDate: Date) => Promise<CalendarEvent | null>;
  onCopyDay?: (date: Date, events: CalendarEvent[], withGuards?: boolean) => void;
  onPasteDay?: (date: Date) => Promise<CalendarEvent[]>;
  onDeleteDay?: (date: Date, events: CalendarEvent[]) => void;
  permissions?: any;
  titleColors?: Record<string, { backgroundColor: string; textColor: string }>;
  isMonthView?: boolean;
  showDuration?: boolean;
  monthViewColumnWidth?: string;
  eventsWithMultipleShifts?: Set<string>;
  dateRange?: { start: Date; end: Date };
  calendarZoom?: number;
}

const PerJobRowView: React.FC<PerJobRowViewProps> = ({
  daySchedules,
  guards,
  handleDayClick,
  handleEventClick,
  onDuplicateEvent,
  onDeleteEvent,
  onCopyShift,
  onCopyShiftWithoutGuards,
  onPasteShift,
  onCopyDay,
  onPasteDay,
  onDeleteDay,
  permissions = { canCreate: false, canUpdate: false, canDelete: false },
  titleColors = {},
  isMonthView = false,
  showDuration = true,
  monthViewColumnWidth,
  eventsWithMultipleShifts = new Set(),
  dateRange,
  calendarZoom = 0.9
}) => {
  const { t } = useLanguage();
  const shiftContextMenu = useShiftContextMenu();
  const { createShift, deleteShift } = useShiftApi();
  const queryClient = useQueryClient();
  const { theme } = useTheme();
  const { intensity } = useColorIntensity();
  const { user, isAdmin, isManager } = useUser();

  // Filter guards: if user is a guard (not admin/manager), only show their own row
  const isGuard = !isAdmin && !isManager;
  const filteredGuards = isGuard && user?.id 
    ? guards.filter(guard => guard.id === user.id)
    : guards;

  // Calculate date range from daySchedules if not provided
  // Extend the range by one day before to find previous work for repo shifts
  const baseDateRange = dateRange || {
    start: daySchedules.length > 0 ? daySchedules[0].date : new Date(),
    end: daySchedules.length > 0 ? daySchedules[daySchedules.length - 1].date : new Date()
  };
  
  const effectiveDateRange = {
    start: subDays(baseDateRange.start, 1), // Include one day before for previous work lookup
    end: baseDateRange.end
  };

  // Fetch all shifts for all guards - fixed: don't call hooks inside map
  // Create stable array of guard IDs for query keys
  const guardIds = filteredGuards.map(g => g.id);
  
  const { data: allGuardShiftsData = {} } = useQuery({
    queryKey: ['all-guard-shifts', guardIds, effectiveDateRange.start.toISOString(), effectiveDateRange.end.toISOString()],
    queryFn: async () => {
      const shiftsMap: { [guardId: string]: CalendarEvent[] } = {};
      
      await Promise.all(
        filteredGuards.map(async (guard) => {
          try {
            const shifts = await fetchAllGuardSchedules(guard.id, effectiveDateRange);
            shiftsMap[guard.id] = shifts || [];
          } catch (error) {
            console.error(`Error fetching shifts for guard ${guard.id}:`, error);
            shiftsMap[guard.id] = [];
          }
        })
      );
      
      return shiftsMap;
    },
    enabled: filteredGuards.length > 0
  });

  // Helper function to get shifts for a specific guard
  const getGuardShifts = (guardId: string) => {
    const allShifts = allGuardShiftsData[guardId] || [];

    // Group shifts by date
    const shiftsByDate: { [key: string]: CalendarEvent[] } = {};
    
    // Initialize with visible days
    daySchedules.forEach(daySchedule => {
      const dateKey = format(daySchedule.date, 'yyyy-MM-dd');
      shiftsByDate[dateKey] = [];
    });

    // Include all fetched shifts, including those from before the visible range
    allShifts.forEach(shift => {
      const shiftDate = fromLocalISOString(shift.start_date);
      const dateKey = format(shiftDate, 'yyyy-MM-dd');
      
      // Initialize the date if it doesn't exist (for previous days)
      if (!shiftsByDate[dateKey]) {
        shiftsByDate[dateKey] = [];
      }
      
      shiftsByDate[dateKey].push(shift);
    });

    return shiftsByDate;
  };

  // Find previous day's work for a guard
  const findPreviousDayWork = (guardId: string, currentDate: Date): CalendarEvent | null => {
    const guardShifts = getGuardShifts(guardId);
    const previousDay = subDays(currentDate, 1);
    const previousDayKey = format(previousDay, 'yyyy-MM-dd');
    
    const previousDayShifts = guardShifts[previousDayKey] || [];
    
    // Return the first shift from the previous day (could be enhanced to find the most relevant one)
    return previousDayShifts.length > 0 ? previousDayShifts[0] : null;
  };

  // Add repo shift based on previous day's work
  const addRepoShift = async (guardId: string, targetDate: Date) => {
    const previousWork = findPreviousDayWork(guardId, targetDate);
    
    if (!previousWork) {
      toast.error(t('noPreviousDayWork') || 'No previous day work found for this guard');
      return;
    }

    // Create repo shift with 1-minute duration starting at 13:00
    const repoStartTime = new Date(targetDate);
    repoStartTime.setHours(13, 0, 0, 0);
    
    const repoEndTime = new Date(repoStartTime);
    repoEndTime.setHours(13, 1, 0, 0);

    const repoShift: CalendarEvent = {
      id: crypto.randomUUID(),
      title: 'ΡΕΠΟ', // Use proper repo title instead of previous work title
      description: `Repo shift based on previous work`,
      start_date: toLocalISOString(repoStartTime),
      end_date: toLocalISOString(repoEndTime),
      assigned_guards: [guardId],
      team_id: previousWork.team_id,
      location: previousWork.location || 'Repo Location',
      created_by: previousWork.created_by,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      color: 'secondary'
    };

    try {
      const result = await createShift(repoShift);
      if (result) {
        toast.success(t('repoShiftCreated') || 'Repo shift created successfully');
        
        // Invalidate and refetch the guard's shifts to update the UI
        queryClient.invalidateQueries({
          queryKey: ['guard-all-shifts', guardId]
        });
        
        // Also trigger the custom event for other components
        document.dispatchEvent(new CustomEvent('refetch-schedules'));
      }
    } catch (error) {
      console.error('Error creating repo shift:', error);
      toast.error(t('errorCreatingRepoShift') || 'Error creating repo shift');
    }
  };

  // Add leave shift function
  const addLeaveShift = async (guardId: string, targetDate: Date, leaveType: string) => {
    const previousWork = findPreviousDayWork(guardId, targetDate);
    
    if (!previousWork) {
      toast.error(t('noPreviousDayWork') || 'No previous day work found for this guard');
      return;
    }

    // Set different time ranges based on leave type
    let leaveStartTime = new Date(targetDate);
    let leaveEndTime = new Date(targetDate);
    
    if (leaveType === 'ΜΗ ΕΡΓΑΣΙΑ') {
      // ΜΗ ΕΡΓΑΣΙΑ: 13:00 to 13:01
      leaveStartTime.setHours(13, 0, 0, 0);
      leaveEndTime.setHours(13, 1, 0, 0);
    } else {
      // Other leave types: 14:01 to 22:00
      leaveStartTime.setHours(14, 1, 0, 0);
      leaveEndTime.setHours(22, 0, 0, 0);
    }

    const leaveShift: CalendarEvent = {
      id: crypto.randomUUID(),
      title: leaveType,
      description: `Leave shift: ${leaveType}`,
      start_date: toLocalISOString(leaveStartTime),
      end_date: toLocalISOString(leaveEndTime),
      assigned_guards: [guardId],
      team_id: previousWork.team_id,
      location: previousWork.location || 'Leave',
      created_by: previousWork.created_by,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      color: 'secondary'
    };

    try {
      const result = await createShift(leaveShift);
      if (result) {
        toast.success(`${leaveType} created successfully`);
        
        // Invalidate and refetch the guard's shifts to update the UI
        queryClient.invalidateQueries({
          queryKey: ['guard-all-shifts', guardId]
        });
        
        // Also trigger the custom event for other components
        document.dispatchEvent(new CustomEvent('refetch-schedules'));
      }
    } catch (error) {
      console.error(`Error creating ${leaveType}:`, error);
      toast.error(`Error creating ${leaveType}`);
    }
  };

  // Handle shift deletion with refresh
  const handleDeleteShift = async (event: CalendarEvent) => {
    const success = await deleteShift(event.id);
    if (success) {
      // Invalidate and refetch the guard's shifts to update the UI
      if (event.assigned_guards && event.assigned_guards.length > 0) {
        const guardId = event.assigned_guards[0];
        queryClient.invalidateQueries({
          queryKey: ['guard-all-shifts', guardId]
        });
      }
      
      // Also call the parent's delete handler if provided
      if (onDeleteEvent) {
        onDeleteEvent(event);
      }
    }
  };

  // Filter guards that have at least one shift
  const guardsWithShifts = filteredGuards.filter(guard => {
    const guardShifts = getGuardShifts(guard.id);
    return Object.values(guardShifts).some(dayShifts => dayShifts.length > 0);
  });

  const formatGuardName = (guard: any) => {
    return guard.full_name || guard.fullName || guard.name || 'Unknown Guard';
  };

  const formatGuardNames = (guardNames?: string[]) => {
    if (!guardNames || guardNames.length === 0) return 'No guards assigned';
    return guardNames.join(', ');
  };

  // Handle day click with paste functionality
  const handleDayClickWithPaste = async (date: Date, e?: React.MouseEvent) => {
    console.log('Day clicked:', date, 'Has copied shift:', shiftContextMenu.hasCopiedShift);
    
    // Check if we have a copied shift to paste
    if (shiftContextMenu.hasCopiedShift) {
      e?.preventDefault();
      e?.stopPropagation();
      const result = await shiftContextMenu.pasteShift(date);
      if (result) {
        console.log('Shift pasted successfully:', result);
      }
    } else {
      handleDayClick(date);
    }
  };

  return (
    <div className={`calendar-per-job-view ${isMonthView ? 'overflow-x-auto' : ''}`}>
      <div className={`${isMonthView ? 'min-w-max' : 'w-full'}`}>
        {/* Header row with dates */}
        <div className="calendar-row-header grid border-b border-border dark:border-gray-700" style={{
          gridTemplateColumns: isMonthView 
            ? `200px repeat(${daySchedules.length}, ${monthViewColumnWidth || '180px'})` 
            : `200px repeat(${daySchedules.length}, minmax(150px, 1fr))`
        }}>
          <div className="calendar-header-cell p-3 font-semibold border-r border-border dark:border-gray-700 bg-muted/50">
            {t('employee') || 'Employee'}
          </div>
          {daySchedules.map((daySchedule) => (
            <ContextMenu key={daySchedule.date.toISOString()}>
              <ContextMenuTrigger asChild>
                <div 
                  className={`calendar-header-cell p-3 text-center font-medium border-r border-border dark:border-gray-700 bg-muted/20 cursor-pointer hover:bg-muted/30 transition-colors ${
                    shiftContextMenu.hasCopiedShift ? 'ring-2 ring-blue-300 bg-blue-50' : ''
                  }`}
                  onClick={(e) => handleDayClickWithPaste(daySchedule.date, e)}
                  title={shiftContextMenu.hasCopiedShift ? 'Click to paste shift' : 'Click to add shift'}
                >
                  <div className="text-sm">
                    {format(daySchedule.date, 'EEE')}
                  </div>
                  <div className="text-lg font-semibold">
                    {format(daySchedule.date, 'd')}
                  </div>
                </div>
              </ContextMenuTrigger>
              <ContextMenuContent>
                {shiftContextMenu.hasCopiedShift && (
                  <ContextMenuItem onClick={() => shiftContextMenu.pasteShift(daySchedule.date)}>
                    {t('paste') || 'Paste'} {shiftContextMenu.copiedShift?.title || 'Shift'}
                  </ContextMenuItem>
                )}
                <ContextMenuItem onClick={() => handleDayClick(daySchedule.date)}>
                  {t('addShift') || 'Add Shift'}
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          ))}
        </div>

        {/* Rows for each guard */}
        {guardsWithShifts.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            {t('noShiftsAssigned') || 'No shifts assigned to guards'}
          </div>
        ) : (
          guardsWithShifts.map((guard) => {
            const GuardRow = ({ guard }: { guard: any }) => {
              const guardShifts = getGuardShifts(guard.id);
              
              return (
                <div 
                  key={guard.id} 
                  className="calendar-row grid border-b border-border dark:border-gray-700 hover:bg-muted/20 transition-colors"
                  style={{
                    gridTemplateColumns: isMonthView 
                      ? `200px repeat(${daySchedules.length}, ${monthViewColumnWidth || '180px'})` 
                      : `200px repeat(${daySchedules.length}, minmax(150px, 1fr))`
                  }}
                >
                  {/* Guard name column */}
                  <ContextMenu>
                    <ContextMenuTrigger asChild>
                      <div className="guard-name-cell p-3 border-r border-border dark:border-gray-700 bg-muted/10 flex items-center cursor-pointer hover:bg-muted/20 transition-colors">
                        <div className="font-medium text-sm">
                          {formatGuardName(guard)}
                        </div>
                      </div>
                    </ContextMenuTrigger>
                    <ContextMenuContent>
                      {daySchedules.map((daySchedule) => (
                        <React.Fragment key={daySchedule.date.toISOString()}>
                          <ContextMenuItem 
                            onClick={() => addRepoShift(guard.id, daySchedule.date)}
                          >
                            {t('addRepo') || 'Add Repo'} - {format(daySchedule.date, 'MMM dd')}
                          </ContextMenuItem>
                          <ContextMenuItem 
                            onClick={() => addLeaveShift(guard.id, daySchedule.date, 'ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ')}
                          >
                            Add ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ - {format(daySchedule.date, 'MMM dd')}
                          </ContextMenuItem>
                          <ContextMenuItem 
                            onClick={() => addLeaveShift(guard.id, daySchedule.date, 'ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ')}
                          >
                            Add ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ - {format(daySchedule.date, 'MMM dd')}
                          </ContextMenuItem>
                          <ContextMenuItem 
                            onClick={() => addLeaveShift(guard.id, daySchedule.date, 'ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ')}
                          >
                            Add ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ - {format(daySchedule.date, 'MMM dd')}
                          </ContextMenuItem>
                          <ContextMenuItem 
                            onClick={() => addLeaveShift(guard.id, daySchedule.date, 'ΜΗ ΕΡΓΑΣΙΑ')}
                          >
                            Add ΜΗ ΕΡΓΑΣΙΑ - {format(daySchedule.date, 'MMM dd')}
                          </ContextMenuItem>
                        </React.Fragment>
                      ))}
                    </ContextMenuContent>
                  </ContextMenu>

                  {/* Event cells for each day */}
                  {daySchedules.map((daySchedule, dayIndex) => {
                    const dateKey = format(daySchedule.date, 'yyyy-MM-dd');
                    const dayEvents = guardShifts[dateKey] || [];

                    return (
                      <ContextMenu key={`${guard.id}-${dateKey}`}>
                        <ContextMenuTrigger asChild>
                          <div 
                            className={`calendar-day-cell p-2 border-r border-border dark:border-gray-700 min-h-[80px] cursor-pointer hover:bg-muted/30 transition-colors ${
                              shiftContextMenu.hasCopiedShift ? 'hover:bg-blue-100' : ''
                            }`}
                            onClick={(e) => handleDayClickWithPaste(daySchedule.date, e)}
                            title={shiftContextMenu.hasCopiedShift ? 'Click to paste shift' : 'Click to add shift'}
                          >
                            <div className="space-y-1">
                              {dayEvents.map((event, eventIndex) => {
                                const eventColors = titleColors[event.title] || generateEventColor(event.title, fromLocalISOString(event.start_date), theme, intensity);
                                
                                return (
                                  <CalendarEventItem
                                    key={event.id}
                                    event={event}
                                    index={eventIndex}
                                    handleEventClick={handleEventClick}
                                    onDuplicateEvent={onDuplicateEvent}
                                    onDeleteEvent={handleDeleteShift}
                                    formatGuardNames={formatGuardNames}
                                    onCopyShift={shiftContextMenu.copyShift}
                                    onCopyShiftNoGuards={shiftContextMenu.copyShiftWithoutGuards}
                                    titleColor={eventColors}
                                    canUpdate={permissions?.canUpdate || false}
                                    canDelete={permissions?.canDelete !== false}
                                    hasMultipleShifts={eventsWithMultipleShifts.has(event.id)}
                                    isPerJobView={true}
                                    translations={{
                                      guards: t('guards') || 'Guards',
                                      duplicate: t('duplicate') || 'Duplicate',
                                      delete: t('delete') || 'Delete',
                                      copy: t('copy') || 'Copy Shift',
                                      copyWithoutGuards: t('copyWithoutGuards') || 'Copy Without Guards',
                                      paste: t('paste') || 'Paste'
                                    }}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        </ContextMenuTrigger>
                        <ContextMenuContent>
                          {shiftContextMenu.hasCopiedShift && (
                            <ContextMenuItem onClick={() => shiftContextMenu.pasteShift(daySchedule.date)}>
                              {t('paste') || 'Paste'} {shiftContextMenu.copiedShift?.title || 'Shift'}
                            </ContextMenuItem>
                          )}
                          <ContextMenuItem onClick={() => handleDayClick(daySchedule.date)}>
                            {t('addShift') || 'Add Shift'}
                          </ContextMenuItem>
                          <ContextMenuItem onClick={() => addRepoShift(guard.id, daySchedule.date)}>
                            {t('addRepo') || 'Add Repo'}
                          </ContextMenuItem>
                          <ContextMenuItem onClick={() => addLeaveShift(guard.id, daySchedule.date, 'ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ')}>
                            Add ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ
                          </ContextMenuItem>
                          <ContextMenuItem onClick={() => addLeaveShift(guard.id, daySchedule.date, 'ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ')}>
                            Add ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ
                          </ContextMenuItem>
                          <ContextMenuItem onClick={() => addLeaveShift(guard.id, daySchedule.date, 'ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ')}>
                            Add ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ
                          </ContextMenuItem>
                          <ContextMenuItem onClick={() => addLeaveShift(guard.id, daySchedule.date, 'ΜΗ ΕΡΓΑΣΙΑ')}>
                            Add ΜΗ ΕΡΓΑΣΙΑ
                          </ContextMenuItem>
                        </ContextMenuContent>
                      </ContextMenu>
                    );
                  })}
                </div>
              );
            };

            return <GuardRow key={guard.id} guard={guard} />;
          })
        )}
      </div>
    </div>
  );
};

export default PerJobRowView;
